import { useT } from '@powerportalspro/react';
import {
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// The sample contacts, near a place: `allowNearSearch` puts a Near button on the grid's toolbar, beside
// the search box. It finds an address with the map provider's own search, or uses the visitor's
// location, and the grid shows only the contacts within the distance chosen, nearest first, with a
// Distance column — on the cards too — that sorts like any other. A column's sort orders them instead;
// the count, the pages and an export cover only them. The layout dropdown offers the map layouts too,
// which draw the circle.
//
// The Near button needs <MapsProvider> from @powerportalspro/react-maps around the app. It reads the
// coordinates in `mapLatitudeColumn` and `mapLongitudeColumn`, an account's or contact's address
// coordinates unless set. The place is kept in the URL with the rest of the grid's state.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.MapShowcase.MapNearbyGridDemo';

const CONTACT_URL = 'demos/contact';

const CONTACTS_VIEW_ID = '6f1a3c8e-2d4b-4e7a-9c15-8b0d2e6f4a37';

// Where each contact is, and who they work for.
const CONTACTS_FETCH_XML = `<fetch>
  <entity name='contact'>
    <attribute name='fullname' />
    <attribute name='parentcustomerid' />
    <attribute name='address1_city' />
    <attribute name='address1_stateorprovince' />
    <order attribute='fullname' />
    <filter>
      <condition attribute='statecode' operator='eq' value='0' />
    </filter>
  </entity>
</fetch>`;

const DISTANCES = [10, 25, 50, 100, 250];

export function MapNearbyGridSample() {
  const t = useT();

  const views = useMemo<ViewDefinition[]>(
    () => [
      {
        id: CONTACTS_VIEW_ID,
        tableName: 'contact',
        displayName: t(`${STRINGS_BASE}.view-contacts`),
        fetchXml: CONTACTS_FETCH_XML,
        columns: [
          { columnName: 'fullname', width: 200 },
          { columnName: 'parentcustomerid', width: 200 },
          { columnName: 'address1_city', width: 150 },
          { columnName: 'address1_stateorprovince', width: 120 },
        ],
      },
    ],
    [t],
  );

  return (
    <MainGrid
      tableName="contact"
      viewIds={[CONTACTS_VIEW_ID]}
      customViewDefinitions={views}
      defaultViewId={CONTACTS_VIEW_ID}
      allowNearSearch
      nearDistances={DISTANCES}
      nearDistance={50}
      allowChangingLayout
      cardImageColumn="entityimage"
      mapHeight="560px"
      persistedStateQueryParameter="contacts"
      pageSize={10}
    >
      <GridToolbarItems>
        <NavigateOpenRecordGridButton urlFor={(record) => `${CONTACT_URL}?contactId=${record.id}`} />
      </GridToolbarItems>
    </MainGrid>
  );
}
